import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, toConsumableArray as _toConsumableArray, asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useReducer, useState, useEffect, useCallback } from 'react';
import * as abReader from '@vp/ab-reader';
import { fireImpression } from '@vp/ab-reader';
import { createCtx } from '../util/createCtx.js';
import { SearchContextReducer, RESET_RESULTS_AND_FACETS, SET_CURRENT_QUERY, CLEAR_RESULTS, SET_ALL_RESULTS_URL, RESET_RESULTS, ADD_RESULTS, ADD_RENDERER, APPLY_FACET, REMOVE_FACET, UPDATE_FACET_SELECTIONS, APPLY_SORT } from './SearchContextReducer.js';
import { ENABLED_AB_TEST_FEATURES } from '../util/abTestConstants.js';
import { getUrlPrefix } from '../util/secondaryLanguageUrlPrefix.js';
import purify from '../node_modules/dompurify/dist/purify.es.js';

var DEFAULT_MIN_QUERY_LENGTH = 1;
var _createCtx = createCtx(),
  _createCtx2 = _slicedToArray(_createCtx, 2),
  useSearchContext = _createCtx2[0],
  SearchContext = _createCtx2[1];
var defaultInitialState = {
  currentQuery: '',
  results: {},
  previousResults: {},
  sectionTrackingDetails: {},
  allResultsUrl: '',
  renderers: {},
  reportedResultPluginCount: 0,
  availableFacets: {},
  appliedFacets: {},
  totalCount: {},
  sortStrategies: {},
  hasToAppendResults: {}
};
var defaultResultDisplayConfiguration = {
  maxResults: Infinity,
  maxResultsPerTaxonomy: {
    "default": Infinity
  },
  taxonomyOrder: [],
  resultTypeOrderingPerTaxonomy: {},
  taxonomyConfiguration: {}
};
var trackedTests = [];
var SearchContextProvider = function SearchContextProvider(_ref) {
  var children = _ref.children,
    _ref$minCharLength = _ref.minCharLength,
    minCharLength = _ref$minCharLength === void 0 ? DEFAULT_MIN_QUERY_LENGTH : _ref$minCharLength,
    _ref$defaultToAwaitin = _ref.defaultToAwaitingResults,
    defaultToAwaitingResults = _ref$defaultToAwaitin === void 0 ? false : _ref$defaultToAwaitin,
    _ref$locale = _ref.locale,
    locale = _ref$locale === void 0 ? 'en-IE' : _ref$locale,
    _ref$resultsPageUrl = _ref.resultsPageUrl,
    resultsPageUrl = _ref$resultsPageUrl === void 0 ? "".concat(getUrlPrefix(locale), "/search") : _ref$resultsPageUrl,
    getResultsPageUrl = _ref.getResultsPageUrl,
    resultDisplayConfiguration = _ref.resultDisplayConfiguration,
    queryString = _ref.queryString,
    resultPlugins = _ref.resultPlugins,
    instanceId = _ref.instanceId,
    applicationName = _ref.applicationName,
    _ref$enabledAbTests = _ref.enabledAbTests,
    enabledAbTests = _ref$enabledAbTests === void 0 ? ENABLED_AB_TEST_FEATURES : _ref$enabledAbTests,
    auth = _ref.auth,
    waitOnTestLoad = _ref.waitOnTestLoad,
    logger = _ref.logger;
  var MAX_RESULTS_PER_TAXONOMY = 1000;
  var initialState = _objectSpread2({}, defaultInitialState);
  if (queryString) {
    initialState.currentQuery = queryString;
  }
  var _useReducer = useReducer(SearchContextReducer, initialState),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    state = _useReducer2[0],
    dispatch = _useReducer2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasInitialSearch = _useState2[0],
    setHasInitialSearch = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedResult = _useState4[0],
    setSelectedResult = _useState4[1]; //0 indicates the input field is selcted. -1 indicates the "all results" link
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    activeResultId = _useState6[0],
    setActiveResultId = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isFocused = _useState8[0],
    setIsFocused = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isFlyoutExpanded = _useState10[0],
    setIsFlyoutExpanded = _useState10[1];
  var _useState11 = useState([]),
    _useState12 = _slicedToArray(_useState11, 2),
    userAbTests = _useState12[0],
    setUserAbTests = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    allTestsLoaded = _useState14[0],
    setAllTestsLoaded = _useState14[1];
  var _useState15 = useState([]),
    _useState16 = _slicedToArray(_useState15, 2),
    filteredUserAbTests = _useState16[0],
    setFilteredUserAbTests = _useState16[1];
  var _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    searchInputHasBeenTouched = _useState18[0],
    setSearchInputHasBeenTouched = _useState18[1];
  useEffect(function () {
    //Webpack 5 is too clever and tries to protect us from the same thing this if statement does by throwing
    //errors if we directly access initialize in any way on abReader. But apparently it doesn't know that
    //abReader2 is the same thing. Webpack is strange.
    var abReader2 = abReader;
    if (abReader2.initialize) {
      abReader2.initialize();
    }
    abReader.whenAvailable(function () {
      var experiments = abReader.getAllExperiments();
      var filteredSearchTestsFromAbTests = experiments.reduce(function (acc, cur) {
        var _enabledAbTests$cur$e, _enabledAbTests$cur$e2;
        if (Object.keys(enabledAbTests).includes(cur.experimentKey) && !enabledAbTests[cur.experimentKey].locale && !enabledAbTests[cur.experimentKey].localeList || ((_enabledAbTests$cur$e = enabledAbTests[cur.experimentKey]) === null || _enabledAbTests$cur$e === void 0 ? void 0 : _enabledAbTests$cur$e.locale) === locale.toLowerCase() || (_enabledAbTests$cur$e2 = enabledAbTests[cur.experimentKey]) !== null && _enabledAbTests$cur$e2 !== void 0 && (_enabledAbTests$cur$e2 = _enabledAbTests$cur$e2.localeList) !== null && _enabledAbTests$cur$e2 !== void 0 && _enabledAbTests$cur$e2.includes(locale.toLowerCase())) {
          return [].concat(_toConsumableArray(acc), [{
            Experiment: cur.experimentKey,
            Variation: cur.variationKey
          }]);
        }
        return acc;
      }, []);
      dispatch({
        type: RESET_RESULTS_AND_FACETS
      });
      setUserAbTests(experiments.map(function (experiment) {
        return {
          Experiment: experiment.experimentKey,
          Variation: experiment.variationKey
        };
      }));
      setFilteredUserAbTests(filteredSearchTestsFromAbTests);
      setAllTestsLoaded(true);
    }, 2500);
  }, [abReader.whenAvailable, abReader.getAllExperiments, enabledAbTests, locale]);
  var checkFocus = function checkFocus() {
    var _document$activeEleme, _document$activeEleme2;
    //We are focused if the search bar has the focus, or if there is a selected result
    if (!!((_document$activeEleme = document.activeElement) !== null && _document$activeEleme !== void 0 && _document$activeEleme.classList.contains('search-bar-input'))) {
      setSelectedResult(0);
      setIsFocused(true);
      return;
    }
    if (selectedResult > 0 || !!((_document$activeEleme2 = document.activeElement) !== null && _document$activeEleme2 !== void 0 && _document$activeEleme2.classList.contains('search-flyout-item'))) {
      setIsFocused(true);
      return;
    }
    setIsFocused(false);
  };
  var preventBlur = function preventBlur(e) {
    //We need to stop the input from blurring - otherwise clicks on flyout items don't register because the flyout closes
    e.preventDefault();
    e.stopPropagation();
  };
  var setQuery = useCallback(function (newQuery) {
    var sanitizedNewQuery = sanitizeQuery(newQuery);
    dispatch({
      type: SET_CURRENT_QUERY,
      value: sanitizedNewQuery
    });
    if (!sanitizedNewQuery.length) {
      dispatch({
        type: CLEAR_RESULTS
      });
    } else {
      dispatch({
        type: RESET_RESULTS_AND_FACETS
      });
    }
    if (sanitizedNewQuery.length >= minCharLength) {
      setHasInitialSearch(true);
    }
    //TODO there should probably be some abstraction layer over searches here so we can impose the character limit on plugged in search sources
    dispatch({
      type: SET_ALL_RESULTS_URL,
      value: getResultsPageUrl ? getResultsPageUrl(sanitizedNewQuery) : "".concat(resultsPageUrl).concat(resultsPageUrl.includes('?') ? '&query=' : '?query=').concat(encodeURIComponent(sanitizedNewQuery))
    });
  }, [dispatch, minCharLength, setHasInitialSearch]);
  var fireImpressionAndStore = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(test) {
      return _regeneratorRuntime().wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (!trackedTests.includes(test.Experiment)) {
              trackedTests.push(test.Experiment);
              fireImpression(test.Experiment, test.Variation);
            }
          case 1:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), []);
  var isCurrentResultSet = true;
  var shouldGetResults = false;
  var getResultsAsync = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(query) {
      var asyncResults, resultPromises, i, resultPlugin, results, _i, _resultPlugin;
      return _regeneratorRuntime().wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            asyncResults = {};
            if (!(!waitOnTestLoad || allTestsLoaded)) {
              _context2.next = 18;
              break;
            }
            resultPromises = [];
            for (i = 0; i < resultPlugins.length; i++) {
              resultPlugin = resultPlugins[i];
              resultPromises.push(resultPlugin.resultsPromise(resultPlugin.pluginId, query, state.appliedFacets[resultPlugin.taxonomyId], state.sortStrategies[resultPlugin.taxonomyId], userAbTests, logger));
            }
            _context2.next = 6;
            return Promise.all(resultPromises);
          case 6:
            results = _context2.sent;
            _i = 0;
          case 8:
            if (!(_i < results.length)) {
              _context2.next = 18;
              break;
            }
            _resultPlugin = resultPlugins[_i];
            _context2.t0 = _resultPlugin.pluginId;
            _context2.next = 13;
            return results[_i];
          case 13:
            _context2.t1 = _context2.sent;
            asyncResults[_resultPlugin.taxonomyId] = {
              id: _context2.t0,
              response: _context2.t1
            };
          case 15:
            _i++;
            _context2.next = 8;
            break;
          case 18:
            return _context2.abrupt("return", asyncResults);
          case 19:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return function getResultsAsync(_x2) {
      return _ref3.apply(this, arguments);
    };
  }();
  var getResults = function getResults(currentQuery, minCharLength, resultPlugins, userAbTests, filteredUserAbTests, currAvailableFacets, appliedFacets, sortStrategies, resetFacets) {
    if ((currentQuery === null || currentQuery === void 0 ? void 0 : currentQuery.length) >= minCharLength && (!waitOnTestLoad || allTestsLoaded)) {
      resultPlugins.forEach(function (resultPlugin) {
        resultPlugin.resultsPromise(resultPlugin.pluginId, currentQuery, appliedFacets[resultPlugin.taxonomyId], sortStrategies[resultPlugin.taxonomyId], userAbTests, logger).then(function (response) {
          if (isCurrentResultSet) {
            dispatch({
              type: ADD_RESULTS,
              taxonomyId: resultPlugin.taxonomyId,
              results: response.results,
              sectionTrackingDetails: response.sectionTrackingDetails,
              availableFacets: resetFacets ? response.availableFacets : Object.keys(response.availableFacets).reduce(function (newFacetsToKeep, currFacet) {
                var _appliedFacets$result;
                if (!((_appliedFacets$result = appliedFacets[resultPlugin.taxonomyId]) !== null && _appliedFacets$result !== void 0 && (_appliedFacets$result = _appliedFacets$result[currFacet]) !== null && _appliedFacets$result !== void 0 && _appliedFacets$result.count)) {
                  newFacetsToKeep[currFacet] = response.availableFacets[currFacet];
                } else {
                  newFacetsToKeep[currFacet] = Object.keys(response.availableFacets[currFacet]).reduce(function (newValuesToKeep, facetValue) {
                    var _currAvailableFacets$;
                    var oldValueCount = (_currAvailableFacets$ = currAvailableFacets[resultPlugin.taxonomyId]) === null || _currAvailableFacets$ === void 0 || (_currAvailableFacets$ = _currAvailableFacets$[currFacet]) === null || _currAvailableFacets$ === void 0 ? void 0 : _currAvailableFacets$[facetValue];
                    var newValueCount = response.availableFacets[currFacet][facetValue];
                    if (oldValueCount && newValueCount > oldValueCount) {
                      newValuesToKeep[facetValue] = newValueCount - oldValueCount;
                    }
                    return newValuesToKeep;
                  }, {});
                }
                return newFacetsToKeep;
              }, {}),
              totalCount: Math.min(response.totalCount || 0, MAX_RESULTS_PER_TAXONOMY),
              shouldReportComplete: true
            });
            if (trackedTests.length < filteredUserAbTests.length) {
              filteredUserAbTests.forEach( /*#__PURE__*/function () {
                var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(test) {
                  return _regeneratorRuntime().wrap(function _callee3$(_context3) {
                    while (1) switch (_context3.prev = _context3.next) {
                      case 0:
                        fireImpressionAndStore(test);
                      case 1:
                      case "end":
                        return _context3.stop();
                    }
                  }, _callee3);
                }));
                return function (_x3) {
                  return _ref4.apply(this, arguments);
                };
              }());
            }
          }
        });
      });
    }
  };

  //This useEffect has an incomplete dependency array because there is a corresponding useEffect for the other dependencies
  useEffect(function () {
    shouldGetResults = true;
    if (shouldGetResults) {
      resultPlugins.forEach(function (plugin) {
        //This resets us to page 0 of results, which is the rightish thing to do if any of the below change, BUT that's really for the calling app to decide
        //so we should implement a better way of retaining what pagination number we are on at any given time if we use this functionality in the future
        plugin.registerPlugin(plugin.pluginId, state.appliedFacets[plugin.taxonomyId], state.sortStrategies[plugin.taxonomyId], filteredUserAbTests, 0, auth, logger);
      });
      getResults(state.currentQuery, minCharLength, resultPlugins, userAbTests, filteredUserAbTests, state.availableFacets, state.appliedFacets, state.sortStrategies, true);
    }
    return function () {
      dispatch({
        type: RESET_RESULTS_AND_FACETS
      });
      isCurrentResultSet = false;
      shouldGetResults = false;
    };
  }, [state.currentQuery, minCharLength, resultPlugins, userAbTests, filteredUserAbTests, auth]); //Todo: figure out how to better handle resultPlugins to preserve facet response on taxonomies that have facet filters selected

  //This useEffect has an incomplete dependency array because there is a corresponding useEffect for the other dependencies
  useEffect(function () {
    shouldGetResults = true;
    if (shouldGetResults) {
      //This resets us to page 0 of results, which is the rightish thing to do if any of the below change, BUT that's really for the calling app to decide
      //so we should implement a better way of retaining what pagination number we are on at any given time if we use this functionality in the future
      resultPlugins.forEach(function (plugin) {
        plugin.registerPlugin(plugin.pluginId, state.appliedFacets[plugin.taxonomyId], state.sortStrategies[plugin.taxonomyId], filteredUserAbTests, 0, auth, logger);
      });
      getResults(state.currentQuery, minCharLength, resultPlugins, userAbTests, filteredUserAbTests, state.availableFacets, state.appliedFacets, state.sortStrategies, false);
    }
    return function () {
      dispatch({
        type: RESET_RESULTS
      });
      isCurrentResultSet = false;
      shouldGetResults = false;
    };
  }, [state.appliedFacets, state.sortStrategies]);
  var appendResults = function appendResults(taxonomyId) {
    resultPlugins.forEach(function (plugin) {
      if (plugin.taxonomyId === taxonomyId) {
        plugin.resultsPromise(plugin.pluginId, state.currentQuery, appliedFacets[plugin.taxonomyId], state.sortStrategies[plugin.taxonomyId], userAbTests, logger).then(function (response) {
          dispatch({
            type: ADD_RESULTS,
            taxonomyId: plugin.taxonomyId,
            results: response.results,
            availableFacets: response.availableFacets,
            totalCount: Math.min(response.totalCount || 0, MAX_RESULTS_PER_TAXONOMY),
            shouldReportComplete: false,
            sectionTrackingDetails: response.sectionTrackingDetails
          });
        });
      }
    });
  };
  var addResults = useCallback(function (results, taxonomyId) {
    hasInitialSearch && dispatch({
      type: ADD_RESULTS,
      results: results,
      taxonomyId: taxonomyId,
      shouldReportComplete: true
    });
  }, [dispatch, hasInitialSearch]);
  var updateResultsPaginationNumber = useCallback(function (taxonomyId, resultsPaginationNumber) {
    var plugin = resultPlugins.find(function (plugin) {
      return plugin.taxonomyId === taxonomyId;
    });
    if (plugin) {
      plugin.registerPlugin(plugin.pluginId, state.appliedFacets[plugin.taxonomyId], state.sortStrategies[plugin.taxonomyId], filteredUserAbTests, resultsPaginationNumber, auth, logger);
      appendResults(plugin.taxonomyId);
    }
  }, [dispatch, state.appliedFacets, state.sortStrategies, userAbTests, auth]);
  var registerTaxonomyRenderer = useCallback(function (renderer, taxonomyId) {
    dispatch({
      type: ADD_RENDERER,
      taxonomyId: taxonomyId,
      renderer: renderer
    });
  }, [dispatch]);
  var availableFacets = useCallback(function (taxonomies) {
    var _taxonomies;
    if (!((_taxonomies = taxonomies) !== null && _taxonomies !== void 0 && _taxonomies.length)) {
      taxonomies = Object.keys(state.availableFacets);
    }
    var facets = {};
    taxonomies.forEach(function (taxonomyId) {
      var taxonomyFacets = state.availableFacets[taxonomyId] || {};
      //Todo: maybe shared facet-merge function here and with the reducer addResults facet functionality?
      Object.keys(taxonomyFacets).forEach(function (facet) {
        if (!facets[facet]) {
          facets[facet] = {};
        }
        Object.keys(taxonomyFacets[facet]).forEach(function (facetValue) {
          if (!facets[facet][facetValue]) {
            facets[facet][facetValue] = 0;
          }
          facets[facet][facetValue] += taxonomyFacets[facet][facetValue];
        });
      });
    });
    return facets;
  }, [state.availableFacets]);
  var appliedFacets = useCallback(function (taxonomies) {
    var _taxonomies2;
    if (!((_taxonomies2 = taxonomies) !== null && _taxonomies2 !== void 0 && _taxonomies2.length)) {
      taxonomies = Object.keys(state.appliedFacets);
    }
    var appliedFacets = {};
    taxonomies.forEach(function (taxonomy) {
      var appliedFacetsOfTaxonomy = state.appliedFacets[taxonomy] || {};
      Object.keys(appliedFacetsOfTaxonomy).forEach(function (facetKey) {
        if (!appliedFacets[facetKey]) appliedFacets[facetKey] = appliedFacetsOfTaxonomy[facetKey];else appliedFacets[facetKey] = new Set([].concat(_toConsumableArray(appliedFacets[facetKey]), _toConsumableArray(appliedFacetsOfTaxonomy[facetKey])));
      });
    });
    return appliedFacets;
  }, [state.appliedFacets]);
  var selectFacet = useCallback(function (facetKey, facetValue, taxonomyId) {
    if (!taxonomyId) {
      taxonomyId = resultPlugins.map(function (plugin) {
        return plugin.taxonomyId;
      });
    }
    dispatch({
      type: APPLY_FACET,
      taxonomyId: taxonomyId,
      facetKey: facetKey,
      facetValue: facetValue
    });
  }, [dispatch]);
  var removeFacet = useCallback(function (facetKey, facetValue, taxonomyId) {
    if (!taxonomyId) {
      taxonomyId = resultPlugins.map(function (plugin) {
        return plugin.taxonomyId;
      });
    }
    dispatch({
      type: REMOVE_FACET,
      taxonomyId: taxonomyId,
      facetKey: facetKey,
      facetValue: facetValue
    });
  }, [dispatch]);
  var updateFacetSelections = useCallback(function (facetsToApply, facetsToRemove, taxonomyId) {
    if (!taxonomyId) {
      taxonomyId = resultPlugins.map(function (plugin) {
        return plugin.taxonomyId;
      });
    }
    dispatch({
      type: UPDATE_FACET_SELECTIONS,
      facetsToApply: facetsToApply,
      facetsToRemove: facetsToRemove,
      taxonomyId: taxonomyId
    });
  }, [dispatch]);
  var setSortStrategy = useCallback(function (sort, taxonomyId) {
    if (!taxonomyId) {
      taxonomyId = resultPlugins.map(function (plugin) {
        return plugin.taxonomyId;
      });
    }
    dispatch({
      type: APPLY_SORT,
      taxonomyId: taxonomyId,
      sort: sort
    });
  }, [dispatch]);
  var getSearchInput = function getSearchInput(element) {
    var _element$closest;
    var searchInput = (_element$closest = element.closest('.search-application')) === null || _element$closest === void 0 ? void 0 : _element$closest.querySelector('.search-bar-input');
    if (searchInput.nodeName.toLowerCase() === 'input') {
      return searchInput;
    }
    return searchInput.querySelector('input');
  };
  var getFlyoutLinks = function getFlyoutLinks(element) {
    var _element$closest2;
    return (_element$closest2 = element.closest('.search-application')) === null || _element$closest2 === void 0 ? void 0 : _element$closest2.getElementsByClassName('search-flyout-item-link');
  };
  var updateSelectedOptionFromLink = function updateSelectedOptionFromLink(link, selectedValue) {
    var optionElement = link.closest('li');
    if (optionElement) {
      optionElement.ariaSelected = selectedValue;
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    var searchInput = getSearchInput(event.target);
    var flyoutLinks = getFlyoutLinks(event.target);
    var resultsCount = flyoutLinks === null || flyoutLinks === void 0 ? void 0 : flyoutLinks.length;
    Array.prototype.forEach.call(flyoutLinks, function (link) {
      updateSelectedOptionFromLink(link, 'false');
    });
    switch (event.code) {
      case 'ArrowDown':
        if (!resultsCount) {
          return;
        }
        event.preventDefault();
        if (selectedResult === -1) {
          setSelectedResult(0);
          searchInput.focus();
          setActiveResultId(undefined);
          return;
        }
        if (selectedResult < resultsCount) {
          setSelectedResult(selectedResult + 1);
          var link = flyoutLinks[selectedResult];
          var option = link.closest('li');
          option.ariaSelected = 'true';
          link === null || link === void 0 || link.focus();
          setActiveResultId(option.id);
          return;
        }
        if (selectedResult === resultsCount) {
          setSelectedResult(1);
          setActiveResultId(undefined);
          searchInput.focus();
          return;
        }
        return;
      case 'ArrowUp':
        if (!resultsCount) {
          return;
        }
        event.preventDefault();
        if (selectedResult === -1) {
          setSelectedResult(resultsCount);
          var _link = flyoutLinks[resultsCount - 1];
          var _option = _link.closest('li');
          _option.ariaSelected = 'true';
          setActiveResultId(_option.id);
          _link.focus();
          return;
        }
        if (selectedResult === 0) {
          setSelectedResult(resultsCount);
          var _link2 = flyoutLinks[resultsCount - 1];
          var _option2 = _link2.closest('li');
          _option2.ariaSelected = 'true';
          setActiveResultId(_option2.id);
          _link2.focus();
          return;
        }
        if (selectedResult === 1) {
          setSelectedResult(0);
          searchInput.focus();
          setActiveResultId(undefined);
          return;
        }
        if (selectedResult > 1) {
          setSelectedResult(selectedResult - 1);
          var _link3 = flyoutLinks[selectedResult - 2];
          var _option3 = _link3.closest('li');
          _option3.ariaSelected = 'true';
          _link3 === null || _link3 === void 0 || _link3.focus();
          setActiveResultId(_option3.id);
          return;
        }
      case 'Enter':
        return;
      default:
        setSelectedResult(0);
        searchInput.focus();
        checkFocus();
        return;
    }
  };
  var externalSearchApi = {
    query: state.currentQuery,
    setQuery: setQuery,
    isFocused: isFocused,
    isFlyoutExpanded: isFlyoutExpanded,
    setIsFlyoutExpanded: setIsFlyoutExpanded,
    checkFocus: checkFocus,
    preventBlur: preventBlur,
    addResults: addResults,
    updateResultsPaginationNumber: updateResultsPaginationNumber,
    results: state.results,
    previousResults: state.previousResults,
    availableFacets: availableFacets,
    totalCount: state.totalCount,
    appliedFacets: appliedFacets,
    selectFacet: selectFacet,
    removeFacet: removeFacet,
    updateFacetSelections: updateFacetSelections,
    setSortStrategy: setSortStrategy,
    locale: locale,
    resultsPageUrl: resultsPageUrl,
    allResultsUrl: state.allResultsUrl,
    selectedResult: selectedResult,
    activeResultId: activeResultId,
    handleKeyDown: handleKeyDown,
    getSearchInput: getSearchInput,
    renderers: state.renderers,
    registerTaxonomyRenderer: registerTaxonomyRenderer,
    resultDisplayConfiguration: _objectSpread2(_objectSpread2({}, defaultResultDisplayConfiguration), resultDisplayConfiguration),
    instanceId: instanceId,
    allResultsReturned: state.currentQuery && state.currentQuery.length >= minCharLength || defaultToAwaitingResults ? resultPlugins.length === state.reportedResultPluginCount : true,
    userAbTests: userAbTests,
    filteredUserAbTests: filteredUserAbTests,
    fireImpression: fireImpressionAndStore,
    applicationName: applicationName,
    searchInputHasBeenTouched: searchInputHasBeenTouched,
    setSearchInputHasBeenTouched: setSearchInputHasBeenTouched,
    sectionTrackingDetails: state.sectionTrackingDetails,
    logger: logger,
    getResultsAsync: getResultsAsync
  };
  return /*#__PURE__*/React__default.createElement(SearchContext, {
    value: externalSearchApi
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "search-context-inner-wrapper"
  }, children));
};
function sanitizeQuery(query) {
  var sanitizedQuery = purify.sanitize(query);
  var urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
  return sanitizedQuery.replace(urlRegex, '');
}

export { DEFAULT_MIN_QUERY_LENGTH, SearchContext, SearchContextProvider, useSearchContext };
//# sourceMappingURL=SearchContextProvider.js.map
